import { Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse } from '@angular/common/http';
import { Observable, EMPTY, catchError, of, timeout } from 'rxjs';
import { Router } from '@angular/router';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly ALLOWED_PATHS = [
    'login', 'signup', 'forget', 'assets', 'rate_search', 'port_search', 'tracking', 'vessel_schedule', 'admin/checker/mailId_status',
    'reset_link', 'signup-otp', 'otp-auth', 'country', 'checker', 'contact_form', 'validate_link/reset_password', 'login_otp',
    '/atmavaan/query_info', '/atmavaan/addition_service_rate/', '/atmavaan/common_search/zipcode',
    '/atmavaan/rate_hint/', '/atmavaan/appInfo', '/common_search/', '/atmavaan/visitor/',
    '/store_booking_info', '/atmavaan/visitor_events/', '/atmavaan/additional_service/',
    '/blogs', '/career', '/file', '/eventlog', '/visitor_rates', '/tariff', '/appInfo', '/new/',
    'v1/api-service/internal/api-products', 'api/v1/api-service/internal/docs'
  ];

  constructor(private route: Router, @Inject(PLATFORM_ID) private platformId: Object, private injector: Injector) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If running on the server, forward the request but cap how long SSR will wait on it —
    // an unbounded backend call here stalls the whole render (and TTFB/FCP/LCP with it).
    if (isPlatformServer(this.platformId)) {
      return next.handle(request).pipe(
        timeout(5000),
        catchError(err => {
          console.warn('SSR request timed out or failed, returning empty response instead of blocking render:', request.url, err?.message || err);
          return of(new HttpResponse({ status: 204, body: null }));
        })
      );
    }

    if ((request.url || '').toLowerCase().includes('pocketoffice.in')) {
      return next.handle(request);
    }

    // On browser: safe to read localStorage / navigate
    if (isPlatformBrowser(this.platformId)) {
      try {
        const token = localStorage.getItem('x-token');

        // Always add timestamps headers for allowed public requests and authenticated requests
        const nowHeader = {
          'current-time': this.formatDateTime(),
          'utc-time': this.formatDateTime(new Date(), true)
        };

        if (token) {
          // Authenticated request
          // Check if this is the tariff URL
          const isTariff = (request.url || '').toLowerCase().includes('/tariff');
          // If it is 'tariff', use empty headers to prevent CORS errors. Otherwise use token + time.
          let headers = isTariff ? {} : { ...nowHeader, 'x-token': token };

          if ((request.url).includes('/chat-service/') || (request.url).includes('/api-service/internal/')) {
            headers = {
              // ...nowHeader,
              'x-token': token,
              'Authorization': "Bearer " + token,
              'X-User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
            }
          } else if ((request.url).includes('/api-service/internal')) {
            headers = {
              // ...nowHeader,
              'x-token': token,
              'Authorization': "Bearer " + token,
            }
          }
          const cloned = request.clone({
            setHeaders: headers
          });
          return next.handle(cloned).pipe(
            catchError(err => {
              // If server returned 5xx, return a harmless 204 response to client
              const isServerError = err && err.status && err.status >= 500 && err.status < 600;
              if (isServerError) {
                console.warn('Suppressed server 5xx for :', cloned, err);
                // Return an empty HttpResponse with status 204 so client (and Lighthouse) sees success
                return of(new HttpResponse({ status: 204, body: null }));
              }
              // rethrow other errors (e.g., network issues) so normal handling occurs
              throw err;
            })
          );
        }

        // No token — allow certain public endpoints through with time headers
        const url = (request.url || '').toLowerCase();
        const allowed = this.ALLOWED_PATHS.some(p => url.includes(p.toLowerCase()));
        if (allowed) {
          // If allowed but is 'tariff', send empty headers. Else send time headers.
          const isTariff = url.includes('/tariff');
          const isAPIDOC = url.includes('/api/v1/api-service/internal');
          const headers = isTariff || isAPIDOC ? {} : nowHeader;
          const cloned = request.clone({ setHeaders: headers });
          return next.handle(cloned);
        }

        // Not allowed & not authenticated: remember redirect and navigate then CANCEL the request
        const currentUrl = this.route.url || '/';
        try {
          localStorage.setItem('redirectUrl', currentUrl);
        } catch (e) {
          // ignore storage error
        }
        // navigate to login then stop the outgoing request
        console.error('TokenInterceptor: no x-token for disallowed path, redirecting to login. Request URL:', request.url, 'Current URL:', location.href);
        // debugger;
        this.closeOpenPopups();
        this.route.navigateByUrl('/auth/login').catch(() => { /* ignore navigation errors */ });
        return EMPTY; // cancels this outgoing request (resolvers/components will not get the failing call)

      } catch (err) {
        // If anything unexpected happens in the interceptor, avoid breaking the request pipeline.
        // Best effort: forward the original request.
        return next.handle(request);
      }
    }

    // Fallback: forward request unchanged
    return next.handle(request);
  }

  // A router navigation doesn't close Material dialogs / bottom sheets, so e.g. the Request Rate panel
  // stayed on top of the login page. Looked up lazily so the interceptor doesn't depend on them at startup.
  private closeOpenPopups(): void {
    try {
      this.injector.get(MatDialog, null)?.closeAll();
    } catch (e) { /* no dialog service here */ }
    try {
      this.injector.get(MatBottomSheet, null)?.dismiss();
    } catch (e) { /* no bottom sheet service here */ }
  }

  formatDateTime(date: Date = new Date(), useUTC: boolean = false): string {
    const yyyy = useUTC ? date.getUTCFullYear() : date.getFullYear();
    const mm = String((useUTC ? date.getUTCMonth() : date.getMonth()) + 1).padStart(2, '0');
    const dd = String(useUTC ? date.getUTCDate() : date.getDate()).padStart(2, '0');
    const hh = String(useUTC ? date.getUTCHours() : date.getHours()).padStart(2, '0');
    const min = String(useUTC ? date.getUTCMinutes() : date.getMinutes()).padStart(2, '0');
    const ss = String(useUTC ? date.getUTCSeconds() : date.getSeconds()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  }
}